// client/src/HomePage.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './Homepage.css';
import logo from './assets/logo128x128.png';
import discordIcon from './assets/discord-icon.svg';
import CreateMapModal from './components/CreateMapModal';
import ImportMapModal from './components/uploadtools/ImportMapModal';
import AccountDropdown from './components/AccountDropdown';
import LoginButton from './components/LoginButton';
import MapCard from './components/MapCard';
import ProfileModal from './components/ProfileModal';

function useMobile(threshold = 800) {
  const [mobile, setMobile] = useState(window.innerWidth <= threshold);
  useEffect(() => {
    const handleResize = () => setMobile(window.innerWidth <= threshold);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [threshold]);
  return mobile;
}

function capitalise(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getDisplayName(map) {
  if (
    !map.name ||
    map.name.trim() === '' ||
    map.name.trim().toLowerCase() === map.game_name?.trim().toLowerCase()
  ) {
    return { main: capitalise(map.game_name), game: null };
  }
  return { main: map.name, game: capitalise(map.game_name) };
}

const IS_DEV = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
const API_BASE = IS_DEV ? 'https://beta.point-atlas.io' : '';

function getPrettyUrlForSeo(map) {
  if (!map || !map.id) return null;

  const mode = (map.mode || '').toLowerCase();
  const isTabletopMode =
    mode === 'vtt' ||
    mode === 'dnd' ||
    mode === 'tabletop';

  const baseSlug =
    isTabletopMode
      ? 'vtt'
      : map.slug ||
        (map.game_name
          ? map.game_name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')
          : null);

  const username =
    map.uploader_username
      ? map.uploader_username.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')
      : null;

  if (!isTabletopMode) {
    if (baseSlug && username && map.id) {
      return `/map/${baseSlug}_${username}_${map.id}`;
    }
    return `/map/${map.id}`;
  }

  if (baseSlug && username && map.id) {
    return `/vtt/${baseSlug}_${username}_${map.id}`;
  }
  return `/vtt/${map.id}`;
}

function HomePage({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobile(800);

  const [maps, setMaps] = useState([]);
  const [favouriteMaps, setFavouriteMaps] = useState([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('votes');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const [votesData, setVotesData] = useState({});
  const [votesLoading, setVotesLoading] = useState({});
  const [favLoading, setFavLoading] = useState({});

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  function handleBmcPopup() {
    const url = "https://www.buymeacoffee.com/Point_Atlas";
    const width = 450;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    window.open(
      url,
      "_blank",
      `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${width},height=${height},left=${left},top=${top}`
    );
  }

  function renderBMACoffeeButton(style = {}) {
    return (
      <button
        onClick={handleBmcPopup}
        className="bmc-btn"
        style={style}
        type="button"
      >
        <span role="img" aria-label="coffee" style={{ fontSize: 19 }}>☕</span>
        Buy me a coffee
      </button>
    );
  }

  useEffect(() => {
    if (user) {
      fetch(`${API_BASE}/api/user/favourites`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => setFavouriteMaps(data))
        .catch(() => setFavouriteMaps([]));
    } else {
      setFavouriteMaps([]);
    }
  }, [user]);

  useEffect(() => {
    let url = `${API_BASE}/api/maps?sort=${sort}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    fetch(url)
      .then(res => res.json())
      .then(data => setMaps(data))
      .catch(() => {});
  }, [sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const createMap = params.get('createMap');
    let changed = false;

    if (error === 'username_taken') {
      setErrorMessage('Username already exists. Please choose a new username.');
      params.delete('error');
      changed = true;
    }

    if (createMap === '1') {
      setShowModal(true);
      params.delete('createMap');
      changed = true;
    }

    if (changed) {
      const newSearch = params.toString();
      navigate(
        newSearch ? `${location.pathname}?${newSearch}` : location.pathname,
        { replace: true }
      );
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!maps.length) return;
    const loadVotes = async () => {
      const newVotesData = {};
      for (let map of maps) {
        try {
          const res = await fetch(`${API_BASE}/api/map-votes/${map.id}/${user?.id || 0}`);
          const data = await res.json();
          newVotesData[map.id] = data;
        } catch (e) {
          newVotesData[map.id] = { total_votes: map.votes || 0, user_vote: 0 };
        }
      }
      setVotesData(newVotesData);
    };
    loadVotes();
  }, [maps, user]);

  const handleVote = async (mapId, vote) => {
    if (!user) return;
    setVotesLoading(v => ({ ...v, [mapId]: true }));
    try {
      await fetch(`${API_BASE}/api/map-votes/${mapId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, vote }),
      });
      const res = await fetch(`${API_BASE}/api/map-votes/${mapId}/${user.id}`);
      const data = await res.json();
      setVotesData(v => ({ ...v, [mapId]: data }));
    } catch {}
    setVotesLoading(v => ({ ...v, [mapId]: false }));
  };

  const handleFavourite = async (mapId, isFavourited) => {
    if (!user) return;
    setFavLoading(l => ({ ...l, [mapId]: true }));
    try {
      await fetch(`${API_BASE}/api/user/favourites/${mapId}`, {
        method: isFavourited ? 'DELETE' : 'POST',
        headers: { "Content-Type": "application/json" },
        credentials: 'include'
      });
      fetch(`${API_BASE}/api/user/favourites`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => setFavouriteMaps(data));
    } catch {}
    setFavLoading(l => ({ ...l, [mapId]: false }));
  };

  const handleSearch = (e) => setSearch(e.target.value);

  const favMapIds = new Set(favouriteMaps.map(m => m.id));

  const filteredMaps = maps.filter(map => !map.parent_map_id);

  let mapsToRender = filteredMaps;
  if (sort === 'votes') {
    mapsToRender = [...filteredMaps].sort((a, b) => {
      const aVotes = (votesData[a.id]?.total_votes ?? a.votes ?? 0);
      const bVotes = (votesData[b.id]?.total_votes ?? b.votes ?? 0);
      if (bVotes !== aVotes) return bVotes - aVotes;
      const aCreated = new Date(a.createdAt || a.created_at || 0).getTime();
      const bCreated = new Date(b.createdAt || b.created_at || 0).getTime();
      return bCreated - aCreated;
    });
  } else if (sort === 'alpha') {
    mapsToRender = [...filteredMaps].sort((a, b) => {
      const aName = (getDisplayName(a).main || '').toLowerCase();
      const bName = (getDisplayName(b).main || '').toLowerCase();
      if (aName < bName) return -1;
      if (aName > bName) return 1;
      return 0;
    });
  }

  const itemListElements = mapsToRender.slice(0, 50).map((map, index) => {
    const display = getDisplayName(map);
    const path = getPrettyUrlForSeo(map);
    if (!path) return null;
    return {
      '@type': 'ListItem',
      position: index + 1,
      name: display.main || 'Interactive map',
      url: `https://point-atlas.io${path}`
    };
  }).filter(Boolean);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Point Atlas Map Directory',
    itemListElement: itemListElements
  };

  const MobileDrawer = () => (
    <>
      <div
        className={`drawer-backdrop${drawerOpen ? ' open' : ''}`}
        onClick={() => setDrawerOpen(false)}
      />
      <nav className={`drawer-menu${drawerOpen ? ' open' : ''}`}>
        <button className="drawer-close-btn" onClick={() => setDrawerOpen(false)}>&times;</button>

        <button
          className="create-map-button"
          style={{
            width: "100%",
            marginBottom: 12,
            fontSize: 17,
            height: 44,
            background: "#ff5959",
            cursor: "pointer"
          }}
          onClick={() => {
            setShowModal(true);
            setDrawerOpen(false);
          }}
        >
          + Create Map
        </button>

        <button
          className="bmc-btn"
          style={{
            width: "100%",
            marginBottom: 16,
            fontSize: 17,
            height: 44,
            justifyContent: 'center'
          }}
          onClick={handleBmcPopup}
        >
          <span role="img" aria-label="coffee" style={{ fontSize: 19 }}>☕</span>
          Buy me a coffee
        </button>

        {!user && (
          <div
            style={{
              width: "100%",
              marginBottom: 22,
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <LoginButton />
            </div>
          </div>
        )}

        {user && (
          <div style={{ width: "100%" }}>
            <AccountDropdown
              user={user}
              setUser={setUser}
              onProfileClick={() => { setDrawerOpen(false); setShowProfileModal(true); }}
              onImportMap={() => { setShowImportModal(true); setDrawerOpen(false); }}
            />
          </div>
        )}
      </nav>
    </>
  );

  return (
    <div className="homepage" style={{ paddingBottom: '60px' }}>
      <Helmet>
        <title>Point Atlas – Interactive Maps for Games, D&D Worlds & Tabletop Adventures</title>
        <meta
          name="description"
          content="Explore and create interactive maps for video games, D&D worlds, fantasy realms and tabletop campaigns. Browse community-made maps with searchable POIs, zones, NPC pathing and VTT tools on Point Atlas."
        />
        <meta
          name="keywords"
          content="
            interactive map platform,
            custom game maps,
            dnd maps,
            d&d world map,
            fantasy map viewer,
            vtt maps,
            tabletop rpg maps,
            online map maker,
            map editor for games,
            rpg interactive maps,
            sandbox game maps,
            multiplayer game map creator,
            indie game maps,
            points of interest map,
            draw zones on maps,
            map sharing platform,
            create game maps online,
            map voting system,
            map commenting,
            user-generated maps,
            custom map icons,
            map creator community,
            npc pathing maps
          "
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Decado Designs" />
        <meta property="og:title" content="Point Atlas – Interactive Maps for Games, D&D & Tabletop" />
        <meta
          property="og:description"
          content="Browse interactive maps for video games, D&D worlds and tabletop campaigns. Discover POIs, quests, settlements and more on Point Atlas."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://point-atlas.io" />
        <meta property="og:image" content="https://point-atlas.io/logo128x128.png" />
        <link rel="canonical" href="https://point-atlas.io" />
        {itemListElements.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>

      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: 18,
          marginBottom: 10,
          userSelect: "none"
        }}
      >
        <img
          src={logo}
          alt="Point Atlas logo"
          style={{
            width: 70,
            height: 70,
            marginBottom: 5,
            filter: 'drop-shadow(0 0 18px #2227)'
          }}
        />
        <h1
          className="brand"
          style={{
            fontSize: "2.7rem",
            fontWeight: 800,
            letterSpacing: "0.09em",
            margin: 0,
            color: "#f8f9fa",
            textShadow: "0 2px 14px #0009, 0 0px 1px #003",
            textAlign: "center"
          }}
        >
          POINT ATLAS
        </h1>
        <p
          style={{
            marginTop: 10,
            marginBottom: 4,
            maxWidth: 820,
            textAlign: "center",
            fontSize: 15,
            color: "#d1d5db"
          }}
        >
          Explore community-made interactive maps for games, D&amp;D worlds and tabletop campaigns. Search POIs and labels, or create and share your own map in minutes.
        </p>
      </div>

      <div className="homepage-header">
        <input
          className="search-bar"
          type="text"
          placeholder="Search maps."
          value={search}
          onChange={handleSearch}
        />

        {!isMobile && (
          <>
            <button
              className="create-map-button"
              style={{
                fontSize: 17,
                height: 44,
                background: "#ff5959",
                cursor: "pointer"
              }}
              onClick={() => setShowModal(true)}
            >
              + Create Map
            </button>
            {renderBMACoffeeButton({ fontSize: 17, height: 44, marginLeft: 12 })}
            <AccountDropdown
              user={user}
              setUser={setUser}
              onProfileClick={() => setShowProfileModal(true)}
              onImportMap={() => setShowImportModal(true)}
            />
          </>
        )}

        {isMobile && (
          <button
            className="hamburger"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
          >
            <span />
            <span />
            <span />
          </button>
        )}
      </div>

      {isMobile && <MobileDrawer />}

      <div style={{ display: 'flex', gap: 16, margin: '16px 0' }}>
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          style={{ fontSize: 15, padding: "5px 6px", height: 32, borderRadius: 6 }}
        >
          <option value="newest">Newest</option>
          <option value="votes">Top Rated</option>
          <option value="alpha">Alphabetical</option>
        </select>
      </div>

      {errorMessage && (
        <div style={{
          position: 'fixed',
          top: 80,
          right: 20,
          backgroundColor: 'rgba(255, 0, 0, 0.9)',
          color: 'white',
          padding: '12px 20px',
          borderRadius: 6,
          zIndex: 1000,
          boxShadow: '0 0 10px rgba(255,0,0,0.5)',
          cursor: 'pointer'
        }}
          onClick={() => setErrorMessage(null)}
        >
          {errorMessage} (click to dismiss)
        </div>
      )}

      <div className="section">
        <h2 style={{ marginBottom: 6 }}>All Maps</h2>
        <div className="maps-grid">
          {mapsToRender.length === 0 ? (
            <div style={{
              color: "#fff",
              background: "#23252b",
              padding: "28px 22px",
              borderRadius: 10,
              textAlign: "center",
              marginTop: 30,
              marginBottom: 24,
              boxShadow: "0 4px 28px #0003"
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 14 }}>
                No maps found for <span style={{ color: "#7bffcb" }}>{search ? `"${search}"` : "this search"}</span>.
              </div>
              <div style={{ fontSize: 16, marginBottom: 16 }}>
                Be the first to create and upload a map!
              </div>
              <button
                onClick={() => setShowModal(true)}
                style={{
                  background: "#40DCA5",
                  color: "#23252b",
                  border: "none",
                  borderRadius: 6,
                  fontWeight: 700,
                  fontSize: 17,
                  padding: "10px 24px",
                  cursor: "pointer"
                }}
              >
                + Create Map
              </button>
            </div>
          ) : (
            mapsToRender.map(map => (
              <MapCard
                key={map.id}
                map={map}
                user={user}
                showVotes={true}
                votes={votesData[map.id]?.total_votes}
                userVote={votesData[map.id]?.user_vote}
                onVote={(id, vote) => handleVote(id, vote)}
                isFavourited={favMapIds.has(map.id)}
                favLoading={favLoading[map.id]}
                onFavourite={() => handleFavourite(map.id, favMapIds.has(map.id))}
                showFavouriteButton={true}
              />
            ))
          )}
        </div>
      </div>

      {showModal && <CreateMapModal onClose={() => setShowModal(false)} user={user} />}

      {showImportModal && (
        <ImportMapModal onClose={() => setShowImportModal(false)} user={user} />
      )}

      {showProfileModal && user && (
        <ProfileModal
          user={user}
          onClose={() => setShowProfileModal(false)}
          onUserUpdate={updatedUser => setUser(updatedUser)}
        />
      )}

      {showLoginPrompt && (
        <div
          className="map-modal-backdrop"
          onClick={() => setShowLoginPrompt(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.78)',
            zIndex: 11000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <div
            className="map-modal-container"
            onClick={e => e.stopPropagation()}
            style={{
              background: '#18192a',
              color: '#fff',
              padding: '24px 28px',
              borderRadius: '14px',
              maxWidth: 360,
              boxShadow: '0 6px 32px #000b, 0 0 0 2px #23272b',
              fontFamily: 'inherit',
              userSelect: 'text',
              textAlign: 'center',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 16 }}>
              Login Required
            </h3>
            <p style={{ marginBottom: 20 }}>
              You need to be logged in to create or import maps.<br />
              This ensures your maps are saved securely in the cloud and linked to your account as the owner.
            </p>
            <button
              onClick={() => setShowLoginPrompt(false)}
              style={{
                background: '#5374e7',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '10px 20px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          width: "100%",
          textAlign: "center",
          marginTop: 60,
          marginBottom: 40,
          fontSize: 13,
          color: "#9aa0ae",
          opacity: 0.7
        }}
      >
        Point Atlas is a community-driven platform for discovering, building and sharing interactive maps for video games, open-world exploration titles, D&D worlds and tabletop RPG campaigns. The goal is simple: give players and creators powerful tools to visualise their worlds, plan efficient routes, track important locations, document discoveries and collaborate on living, evolving maps.

Every map on Point Atlas supports searchable POIs, custom labels, colour-coded categories, routes, zones, screenshots, notes and player markers. Whether you’re exploring a survival game, navigating a space sim, hunting for collectibles, planning a D&D session or mapping out a fantasy continent, the platform adapts to the way you play. Zoom smoothly between regions, toggle categories to declutter the view, or click any POI to reveal additional details contributed by the community.

Creators and GMs can build maps from scratch, upload their own world images, place POIs, outline regions, draw zones and create multi-floor layouts for more complex environments. Advanced tools allow you to design custom markers, link areas with teleport zones, animate routes with pathing tokens and track personal notes privately in your browser. Many players also create offline maps — maps stored entirely in the browser — giving them full creative freedom without needing a public database entry.

Point Atlas is built around collaboration and shared discovery. Players can comment on maps, mark POIs as found, request additions, vote for their favourite maps and follow the ones they use regularly. Popular maps receive ongoing updates from their creators, especially when games receive new patches, expansions or balancing changes. With thousands of POIs contributed by players, GMs, modders and developers, the platform grows continuously as communities refine and expand the worlds they care about.

Whether you're a player planning efficient routes, a streamer preparing content, a GM designing a campaign world or a creator mapping out a fully custom setting, Point Atlas provides a clean, fast and powerful way to explore, build and share interactive maps.
      </div>

      <div
        style={{
          position: 'fixed',
          bottom: 10,
          right: 10,
          backgroundColor: '#5865F2',
          borderRadius: '50%',
          width: 48,
          height: 48,
          cursor: 'pointer',
          userSelect: 'none',
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={() => window.open('https://discord.gg/5Btm4CHaQf', '_blank')}
        title="Join our Discord server"
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            window.open('https://discord.gg/5Btm4CHaQf', '_blank');
          }
        }}
      >
        <img
          src={discordIcon}
          alt="Discord"
          style={{ width: 24, height: 24, filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.3))' }}
          draggable={false}
          loading="lazy"
        />
      </div>
    </div>
  );
}

export default HomePage;
